import Popover, { positionBounded } from '@components/Popover/Popover';
import QRCode, { type QRCodeHandle } from '@components/QRCode/QRCode';
import { faLinkedinIn } from '@fortawesome/free-brands-svg-icons';
import {
  type IconDefinition,
  faAngleRight,
  faComment,
  faEnvelope,
  faLink,
  faShare,
  faTimes,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { analytics } from '@modules/analytics';
import { getCardUrl } from '@modules/card-urls/card-urls';
import { useGetQRCodeUrl } from '@modules/card-urls/use-get-qr-card-url';
import { copyToClipboard } from '@modules/helpers';
import { removeUrlScheme } from '@modules/url-helpers';
import { Button } from '@ui-lib';
import { isMac } from 'app-next/[workspace]/cards/util';
import { useRef, useState } from 'react';

import * as styles from './ShareSheet.css';
import type { Card_Share_Methods } from '.cache/__types__';

type ShareOptions = 'email' | 'iMessage' | 'linkedIn';

const shareOptions = [
  {
    type: 'email',
    accent: '#181D24',
    text: 'Share via Email',
    icon: faEnvelope,
  },
  {
    type: 'iMessage',
    accent: '#6EDA4F',
    text: 'Share via iMessage',
    icon: faComment,
  },
  {
    type: 'linkedIn',
    accent: '#3367AA',
    text: 'Share via LinkedIn',
    icon: faLinkedinIn,
  },
] satisfies {
  type: ShareOptions;
  accent: `#${string}`;
  text: string;
  icon: IconDefinition;
}[];

type WorkspaceDetails = {
  name?: string;
  id: string;
};

type ShareSheetProps = {
  cardId: string;
  headerTitle: string;
  onShareSuccess?: (method: Card_Share_Methods) => void;
  generatePrefilledMessage: (cardUrl: string) => {
    subject?: string;
    body: string;
  };
  isOpen: boolean;
  targetNode?: HTMLElement;
  whitelistedShareOptions?: ShareOptions[];
  workspaces?: WorkspaceDetails[];
  onWorkspaceShare?: (workspace: WorkspaceDetails) => void;
  toggleOpenState: (isOpen?: boolean) => void;
  qrCodeSettings: {
    image?: string;
    title: string;
  };
};

export const ShareSheet: React.FC<ShareSheetProps> = ({
  workspaces,
  onWorkspaceShare,
  onShareSuccess,
  cardId,
  headerTitle,
  targetNode,
  isOpen,
  toggleOpenState,
  qrCodeSettings,
  generatePrefilledMessage,
  whitelistedShareOptions,
}) => {
  const { getQrCardUrl } = useGetQRCodeUrl();
  const { title: qrCodeTitle, image: qrCodeImage } = qrCodeSettings;
  const qrCodeRef = useRef<QRCodeHandle>(null);

  const cardShareDisplayUrl = getCardUrl(cardId);
  const cardShareUrl = getCardUrl(cardId, { blinqSource: 'db' });
  const cardShareQRUrl = getQrCardUrl(cardId, { blinqSource: 'db' });

  const [showCopyLinkSuccess, setShowCopyLinkSuccess] = useState(false);
  const [showDownloadQrSuccess, setShowDownloadQrSuccess] = useState(false);

  const compatibleShareOptions = !isMac()
    ? shareOptions.filter(({ type }) => type !== 'iMessage')
    : shareOptions;

  const allowedShareOptions = whitelistedShareOptions
    ? compatibleShareOptions.filter(({ type }) => whitelistedShareOptions?.includes(type))
    : compatibleShareOptions;

  const handleClose = () => {
    toggleOpenState(false);
  };

  const handleDownloadQr = async () => {
    analytics.track({ name: 'qrcode_downloaded', properties: { page: 'share-card-popover' } });
    const filename = `${cardId}-qrcode`;
    qrCodeRef.current?.downloadQRCode(filename.concat('.png'));

    onShareSuccess?.('qr_download');
    setShowDownloadQrSuccess(true);
    setTimeout(() => setShowDownloadQrSuccess(false), 2000);
  };

  const handleCopyLink = async () => {
    await copyToClipboard(cardShareUrl);
    onShareSuccess?.('copy_link');
    setShowCopyLinkSuccess(true);
    setTimeout(() => setShowCopyLinkSuccess(false), 2000);
  };

  const handleShareCard = (shareVia: string) => {
    switch (shareVia) {
      case 'email':
        shareViaEmail();
        break;
      case 'iMessage':
        shareViaIMessage();
        break;
      case 'linkedIn':
        shareViaLinkedIn();
        break;
      default:
        return;
    }
  };

  const shareViaEmail = () => {
    onShareSuccess?.('email');
    const emailSubject = generatePrefilledMessage(cardShareUrl).subject || `Blinq Card inside!`;

    window.open(`mailto:?subject=${emailSubject}&body=${generatePrefilledMessage(cardShareUrl).body}`);
  };

  const shareViaIMessage = () => {
    onShareSuccess?.('imessage');
    window.open(`sms:?&body=${generatePrefilledMessage(cardShareUrl).body}`);
  };

  const shareViaLinkedIn = () => {
    onShareSuccess?.('linkedin');
    const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${cardShareUrl}`;
    window.open(linkedInShareUrl, '_blank', 'height=632,width=400');
  };

  return (
    <Popover
      show={isOpen}
      onClose={handleClose}
      positioner={positionBounded({ xPreference: 'left', alignX: 'cover', offsetY: 18 })}
      preventOutsideInteraction
      drawFocus
      size="large"
      targetNode={targetNode}
    >
      <div className={styles.PopoverStyle}>
        <div className={styles.popoverHeader}>
          <div className={styles.headerTitle}>{headerTitle}</div>
          <div className={styles.headerCloseIcon}>
            <Button
              onClick={handleClose}
              variant="text"
              size="regular"
              data-testid="share-sheet-close-button"
            >
              <FontAwesomeIcon icon={faTimes} className={styles.headerCloseIcon} />
            </Button>
          </div>
        </div>

        <div className={styles.cardInfo}>
          <button onClick={handleCopyLink} className={`${styles.cardInfoBtn} ${styles.shareCardButtons}`}>
            <div className={styles.cardInfoLink}>
              <FontAwesomeIcon icon={faLink} />
              <div className={styles.linkText}>
                <input className={styles.linkInput} value={removeUrlScheme(cardShareDisplayUrl)} readOnly />
              </div>
            </div>

            {showCopyLinkSuccess ? (
              <div className={`${styles.actionText} ${styles.actionSuccessState}`}>Copied</div>
            ) : (
              <div className={`${styles.actionText} ${styles.actionInitialState}`}>Copy</div>
            )}
          </button>

          <button onClick={handleDownloadQr} className={`${styles.cardInfoBtn} ${styles.shareCardButtons}`}>
            <div className={styles.qrContainer}>
              <QRCode
                size={500}
                displaySize={64}
                value={cardShareQRUrl}
                imageSrc={qrCodeImage || ''}
                ref={qrCodeRef}
              />
              <div className={styles.qrDetails}>
                <div className={styles.qrTitle}>{qrCodeTitle}</div>
                <div className={styles.qrSubscript}>QR code</div>
              </div>
              {showDownloadQrSuccess ? (
                <div className={`${styles.actionText} ${styles.actionSuccessState}`}>Downloaded</div>
              ) : (
                <div className={`${styles.actionText} ${styles.actionInitialState}`}>Download</div>
              )}
            </div>
          </button>
          {workspaces && (
            <div className={styles.workspacesShareSectionContainer}>
              <div
                className={styles.workspacesShareSection({
                  scrolling: workspaces.length > 2,
                })}
              >
                {workspaces.map((ws) => (
                  <button
                    key={ws.id}
                    onClick={() => {
                      handleClose();
                      onWorkspaceShare?.(ws);
                    }}
                    className={`${styles.cardInfoBtn} ${styles.shareCardButtons}`}
                  >
                    <div className={styles.cardInfoLink}>
                      <FontAwesomeIcon icon={faShare} />
                      <div className={styles.linkText}>
                        <input className={styles.linkInput} value={`Share with ${ws.name}`} readOnly />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              {workspaces.length > 2 && <div className={styles.workspacesShareSectionFade} />}
            </div>
          )}
        </div>
        <div className={styles.shareLinksContainer}>
          <div className={styles.shareLinksTitle}>Share via</div>
          <div className={styles.shareLinksList}>
            {allowedShareOptions.map((option, i) => {
              return (
                <button
                  // biome-ignore lint/suspicious/noArrayIndexKey: historic
                  key={i}
                  className={styles.shareCardButtons}
                  onClick={() => handleShareCard(option.type)}
                >
                  <div className={styles.shareInfo}>
                    <div className={styles.shareIcon} style={{ background: option.accent }}>
                      <FontAwesomeIcon icon={option.icon} />
                    </div>
                    <div className={styles.shareText}>{option.text}</div>
                    <div className={styles.shareArrow}>
                      <FontAwesomeIcon icon={faAngleRight} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </Popover>
  );
};
